import { analytics } from '../firebase';
import { logEvent, setUserProperties, setUserId } from 'firebase/analytics';

// Check if analytics is available and properly initialized
const isAnalyticsAvailable = () => {
  try {
    return analytics && typeof analytics === 'object';
  } catch (error) {
    console.warn('[Analytics] Analytics not available:', error);
    return false;
  }
};

// Session tracking
let sessionId = null;
let sessionStartTime = null;
let currentPage = null;
let pageStartTime = null;

// Generate unique session ID
const generateSessionId = () => {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Initialize session
const initializeSession = () => {
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStartTime = Date.now();
    
    // Track session start
    trackEvent('session_start', {
      session_id: sessionId,
      timestamp: sessionStartTime
    });
  }
  return sessionId;
};

// End session
const endSession = () => {
  if (sessionId && sessionStartTime) {
    const sessionDuration = Date.now() - sessionStartTime;
    
    trackEvent('session_end', {
      session_id: sessionId,
      session_duration: sessionDuration,
      timestamp: Date.now()
    });
    
    sessionId = null;
    sessionStartTime = null;
  }
};

// Core tracking functions
export const trackPageView = (pageName, parameters = {}) => {
  try {
    if (!isAnalyticsAvailable()) {
      console.log(`[Analytics] Page view (offline): ${pageName}`, parameters);
      return;
    }
    
    initializeSession();
    
    // End previous page timing
    if (currentPage && pageStartTime) {
      const timeOnPage = Date.now() - pageStartTime;
      trackEvent('page_time', {
        page: currentPage,
        time_on_page: timeOnPage,
        session_id: sessionId
      });
    }
    
    // Start new page timing
    currentPage = pageName;
    pageStartTime = Date.now();
    
    logEvent(analytics, 'page_view', {
      page_title: pageName,
      session_id: sessionId,
      timestamp: Date.now(),
      ...parameters
    });
    
    console.log(`[Analytics] Page view: ${pageName}`, parameters);
  } catch (error) {
    console.error('[Analytics] Error tracking page view:', error);
  }
};

export const trackEvent = (eventName, parameters = {}) => {
  try {
    if (!isAnalyticsAvailable()) {
      console.log(`[Analytics] Event (offline): ${eventName}`, parameters);
      return;
    }
    
    initializeSession();
    
    logEvent(analytics, eventName, {
      session_id: sessionId,
      timestamp: Date.now(),
      ...parameters
    });
    
    console.log(`[Analytics] Event: ${eventName}`, parameters);
  } catch (error) {
    console.error(`[Analytics] Error tracking event ${eventName}:`, error);
  }
};

// User identification
export const setAnalyticsUserId = (userId) => {
  try {
    if (!isAnalyticsAvailable()) {
      console.log(`[Analytics] Set user ID (offline): ${userId}`);
      return;
    }
    
    setUserId(analytics, userId);
    setUserProperties(analytics, {
      user_id: userId,
      session_id: sessionId
    });
    
    trackEvent('user_login', { user_id: userId });
  } catch (error) {
    console.error('[Analytics] Error setting user ID:', error);
  }
};

// FERS-specific tracking functions
export const trackUserCalculation = (calculationType, parameters = {}) => {
  trackEvent('calculation_performed', {
    calculation_type: calculationType,
    ...parameters
  });
};

export const trackScenarioAction = (action, scenarioData = {}) => {
  trackEvent('scenario_action', {
    action: action, // 'create', 'edit', 'delete', 'compare'
    scenario_count: scenarioData.scenarioCount || 1,
    pension_type: scenarioData.pensionType,
    retirement_year: scenarioData.retirementYear,
    ...scenarioData
  });
};

export const trackBioDataCompletion = (completionPercentage, bioData = {}) => {
  trackEvent('bio_data_completion', {
    completion_percentage: completionPercentage,
    has_grade: !!bioData.currentGrade,
    has_step: !!bioData.currentStep,
    has_locality: !!bioData.locality,
    has_dob: !!bioData.dateOfBirth,
    has_tsp: !!bioData.currentTspBalance
  });
};

export const trackCalculationResults = (resultType, parameters = {}) => {
  trackEvent('calculation_results', {
    result_type: resultType, // 'pension', 'tsp', 'social_security', 'total'
    ...parameters
  });
};

export const trackExportAction = (exportType, parameters = {}) => {
  trackEvent('export_action', {
    export_type: exportType, // 'csv', 'pdf', 'print'
    data_type: parameters.dataType, // 'results', 'scenarios', 'charts'
    ...parameters
  });
};

export const trackErrorOccurrence = (errorType, errorDetails = {}) => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorDetails.message,
    error_component: errorDetails.component,
    error_stack: errorDetails.stack?.substring(0, 500), // Limit stack trace length
    ...errorDetails
  });
};

export const trackFormInteraction = (formName, fieldName, action, value = null) => {
  trackEvent('form_interaction', {
    form_name: formName,
    field_name: fieldName,
    action: action, // 'focus', 'blur', 'change', 'submit', 'error'
    field_value: value?.toString().substring(0, 100), // Limit value length for privacy
    timestamp: Date.now()
  });
};

export const trackChartInteraction = (chartType, action, parameters = {}) => {
  trackEvent('chart_interaction', {
    chart_type: chartType,
    action: action, // 'hover', 'click', 'zoom', 'filter', 'export'
    ...parameters
  });
};

export const trackFeedbackSubmission = (feedbackType, rating, parameters = {}) => {
  trackEvent('feedback_submitted', {
    feedback_type: feedbackType,
    rating: rating,
    has_message: !!parameters.message,
    page: currentPage,
    session_id: sessionId,
    ...parameters
  });
};

export const trackUserJourney = (milestone, parameters = {}) => {
  trackEvent('user_journey', {
    milestone: milestone, // 'landing', 'bio_started', 'bio_completed', 'scenario_created', 'results_viewed'
    session_id: sessionId,
    time_from_start: sessionStartTime ? Date.now() - sessionStartTime : 0,
    ...parameters
  });
};

// Performance tracking
export const trackPerformance = (metricName, value, parameters = {}) => {
  try {
    if (!isAnalyticsAvailable()) {
      console.log(`[Analytics] Performance (offline): ${metricName} = ${value}`, parameters);
      return;
    }
    
    trackEvent('performance_metric', {
      metric_name: metricName,
      metric_value: value,
      page: currentPage,
      ...parameters
    });
  } catch (error) {
    console.error(`[Analytics] Error tracking performance ${metricName}:`, error);
  }
};

// User engagement tracking
export const trackEngagement = (engagementType, parameters = {}) => {
  trackEvent('user_engagement', {
    engagement_type: engagementType, // 'scroll', 'click', 'hover', 'input'
    page: currentPage,
    session_id: sessionId,
    ...parameters
  });
};

// Exit intent tracking
export const trackExitIntent = (exitReason, parameters = {}) => {
  trackEvent('exit_intent', {
    exit_reason: exitReason,
    page: currentPage,
    time_on_page: pageStartTime ? Date.now() - pageStartTime : 0,
    session_duration: sessionStartTime ? Date.now() - sessionStartTime : 0,
    ...parameters
  });
};

// Bounce tracking
export const trackBounce = (bounceType, parameters = {}) => {
  trackEvent('bounce_detected', {
    bounce_type: bounceType, // 'quick_exit', 'no_interaction', 'error_exit'
    page: currentPage,
    time_on_site: sessionStartTime ? Date.now() - sessionStartTime : 0,
    ...parameters
  });
};

// Initialize analytics when module loads
if (typeof window !== 'undefined') {
  // Track page unload
  window.addEventListener('beforeunload', () => {
    endSession();
  });
  
  // Track visibility change (tab switching)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      trackEvent('page_hidden', {
        page: currentPage,
        time_on_page: pageStartTime ? Date.now() - pageStartTime : 0
      });
    } else {
      trackEvent('page_visible', {
        page: currentPage
      });
    }
  });
  
  // Track focus/blur for engagement
  window.addEventListener('focus', () => {
    trackEngagement('window_focus');
  });
  
  window.addEventListener('blur', () => {
    trackEngagement('window_blur');
  });
}

// Export session utilities
export const getSessionId = () => sessionId;
export const getSessionStartTime = () => sessionStartTime;
export const getCurrentPage = () => currentPage;

// Privacy controls
export const setAnalyticsConsent = (consent) => {
  if (consent) {
    initializeSession();
    trackEvent('analytics_consent_granted');
  } else {
    trackEvent('analytics_consent_denied');
    endSession();
  }
  
  // Store consent in localStorage
  localStorage.setItem('analytics_consent', consent.toString());
};

export const getAnalyticsConsent = () => {
  const consent = localStorage.getItem('analytics_consent');
  return consent === 'true';
};

export default {
  trackPageView,
  trackEvent,
  trackUserCalculation,
  trackScenarioAction,
  trackBioDataCompletion,
  trackCalculationResults,
  trackExportAction,
  trackErrorOccurrence,
  trackFormInteraction,
  trackChartInteraction,
  trackFeedbackSubmission,
  trackUserJourney,
  trackPerformance,
  trackEngagement,
  trackExitIntent,
  trackBounce,
  setAnalyticsUserId,
  setAnalyticsConsent,
  getAnalyticsConsent,
  getSessionId,
  getSessionStartTime,
  getCurrentPage
};